import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, of, switchMap, tap } from 'rxjs'
import { PhotoDto } from '../models/photo-dto'
import { environment } from '../../environments/environment'
import { Materiel } from '../models/materiel'
import { PieceJointeService } from './piece-jointe.service'
import { EvenementDto } from '../models/evenement-dto'
import { DataSharedService } from './data-shared.service'

@Injectable({
  providedIn: 'root',
})
export class PhotoService {
  private http = inject(HttpClient);
  private pieceJointeService = inject(PieceJointeService);
  private dataSharedService = inject(DataSharedService);


  getById(photoId: number) {
    if (this.dataSharedService.photosUrls.getValue().has(photoId)) {
      return of({ lambdaUrl: this.dataSharedService.photosUrls.getValue().get(photoId), id: photoId } as PhotoDto)
    } else {
      return this.doGetById(photoId).pipe(
        tap((value) => {
          if (value?.lambdaUrl) {
            const map = this.dataSharedService.photosUrls.getValue()
            map.set(photoId, value.lambdaUrl)
            this.dataSharedService.photosUrls.next(map)
          }
        })
      )
    }
  }

  getMaterielPhoto(materiel: Materiel): Observable<Partial<PhotoDto>> {
    if (materiel.idPhotoMateriel) {
      return this.getById(materiel.idPhotoMateriel)
    } else if (materiel.categorie?.photoCategorieId) {
      return this.getById(materiel.categorie.photoCategorieId)
    }
    return of({ lambdaUrl: null })
  }

  deleteById(photoId: number): Observable<number> {
    const path = `${environment.baseUrl}/pieceJointePhotos/delete/` + photoId
    return this.http.delete<number>(path)
  }

  uploadMaterielPhoto(materielId: number, photo: File): Observable<Materiel> {
    const sanitizedPhoto = this.pieceJointeService.sanitizeFile(photo)
    let filename: string
    return this.pieceJointeService.uploadPieceJointe(sanitizedPhoto).pipe(
      tap((pieceJointeResponse) => (filename = pieceJointeResponse.filename)),
      switchMap((pieceJointeResponse) => this.pieceJointeService.uploadPieceJointeToS3(sanitizedPhoto, pieceJointeResponse)),
      switchMap((pieceJointeS3Response) => {
        const fileName: string = sanitizedPhoto.name
        const lambdaId: string = filename

        const path = `${environment.baseUrl}/pieceJointePhotos/savePhotoMateriel/` + materielId

        return this.http.post<Materiel>(path, { fileName, lambdaId })
      })
    )
  }

  uploadEvenementPhoto(evenementId: number, photo: File): Observable<EvenementDto> {
    const sanitizedPhoto = this.pieceJointeService.sanitizeFile(photo)
    let filename: string

    return this.pieceJointeService.uploadPieceJointe(sanitizedPhoto).pipe(
      tap((pieceJointeResponse) => (filename = pieceJointeResponse.filename)),
      switchMap((pieceJointeResponse) => this.pieceJointeService.uploadPieceJointeToS3(sanitizedPhoto, pieceJointeResponse)),
      switchMap((pieceJointeS3Response) => {
        const fileName: string = sanitizedPhoto.name
        const lambdaId: string = filename

        const path = `${environment.baseUrl}/pieceJointePhotos/evenement/` + evenementId

        return this.http.post<EvenementDto>(path, { fileName, lambdaId })
      })
    )
  }

  private doGetById(photoId: number): Observable<PhotoDto> {
    const path = `${environment.baseUrl}/pieceJointePhotos/download/` + photoId
    return this.http.get<PhotoDto>(path)
  }
}
